// 3D in-scene info card — Phase 2 polish.
//
// Problem this solves: in immersive-vr (Quest 3, Vision Pro), HTML overlays
// are invisible — the operator can click an info hotspot but the modal that
// describes the sofa / lighting / material never appears. Today this means
// info hotspots are effectively dead in VR.
//
// Solution: a tiny PlayCanvas entity with a plane mesh + canvas-baked
// emissive texture, positioned ~2 m in front of the camera and billboarded
// to face it. Auto-hides after AUTO_HIDE_MS so the operator doesn't end up
// with stale cards floating in the scene.
//
// Activation rule (set by App): only shown when XrManager.isActive() is
// true — outside VR, the existing openInfoModal() HTML modal still wins.
// Avoids duplicating UI surfaces in the common-case 2D demo.

import * as pc from 'playcanvas';
import type { InfoHotspot } from '../app/types';

const LAYER_NAME = 'info-cards-3d';
const CARD_DISTANCE_M = 2.0;        // metres in front of the camera
const CARD_WIDTH_M = 1.6;
const CARD_HEIGHT_M = 0.9;
const CANVAS_W = 1024;
const CANVAS_H = 576; // 16:9 to match card aspect
const AUTO_HIDE_MS = 10_000;

export class InfoCard3D {
  private readonly app: pc.AppBase;
  private readonly camera: pc.Entity;
  private readonly entity: pc.Entity;
  private readonly material: pc.StandardMaterial;
  private readonly texture: pc.Texture;
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly layer: pc.Layer;
  private visible = false;
  private hideTimer: number | null = null;
  private readonly tmpVec3 = new pc.Vec3();

  constructor(app: pc.AppBase, camera: pc.Entity) {
    this.app = app;
    this.camera = camera;

    // Dedicated layer above WORLD + hotspots so the card paints on top.
    const existing = app.scene.layers.getLayerByName(LAYER_NAME);
    if (existing) {
      this.layer = existing;
    } else {
      this.layer = new pc.Layer({ name: LAYER_NAME });
      app.scene.layers.push(this.layer);
    }
    if (camera.camera) {
      const camLayers = camera.camera.layers ?? [];
      if (!camLayers.includes(this.layer.id)) {
        camera.camera.layers = [...camLayers, this.layer.id];
      }
    }

    // Backing 2D canvas where we draw title + body. Same canvas re-used on
    // every show() — `texture.upload()` pushes the new pixels to the GPU.
    this.canvas = document.createElement('canvas');
    this.canvas.width = CANVAS_W;
    this.canvas.height = CANVAS_H;
    const ctx = this.canvas.getContext('2d');
    if (!ctx) throw new Error('[infoCard3D] 2D canvas context unavailable');
    this.ctx = ctx;

    this.texture = new pc.Texture(app.graphicsDevice, {
      width: CANVAS_W,
      height: CANVAS_H,
      format: pc.PIXELFORMAT_R8_G8_B8_A8,
      mipmaps: false,
      addressU: pc.ADDRESS_CLAMP_TO_EDGE,
      addressV: pc.ADDRESS_CLAMP_TO_EDGE,
    });
    this.texture.setSource(this.canvas);

    // Emissive material so the card reads bright in VR (panorama scenes are
    // unlit anyway). useLighting=false matches the panorama sphere pattern.
    this.material = new pc.StandardMaterial();
    this.material.useLighting = false;
    this.material.useFog = false;
    this.material.useTonemap = false;
    this.material.useSkybox = false;
    this.material.diffuse = new pc.Color(0, 0, 0);
    this.material.emissive = new pc.Color(1, 1, 1);
    this.material.emissiveMap = this.texture;
    this.material.opacity = 0.95;
    this.material.blendType = pc.BLEND_NORMAL;
    this.material.depthWrite = false;
    this.material.depthTest = false; // always render on top — important in VR
    this.material.update();

    // Plane entity sized in metres. Default PlayCanvas plane is XZ-oriented;
    // we use a 'plane' primitive and rotate -90° about X so it faces +Z by
    // default, then billboard reorients each frame.
    this.entity = new pc.Entity('info-card-3d');
    this.entity.addComponent('render', {
      type: 'plane',
      material: this.material,
      layers: [this.layer.id],
      castShadows: false,
      receiveShadows: false,
    });
    this.entity.setLocalScale(CARD_WIDTH_M, 1, CARD_HEIGHT_M);
    this.entity.setLocalEulerAngles(-90, 0, 0);
    this.entity.enabled = false;
    app.root.addChild(this.entity);

    // Billboard the card to the camera every frame while visible.
    app.on('update', this.tick);
  }

  /**
   * Display the card with the hotspot's title + description. Position is
   * computed from the camera's current look direction — the card spawns
   * 2 m in front of where the operator's gaze landed.
   *
   * Auto-hides after AUTO_HIDE_MS unless hide() is called sooner.
   */
  show(hotspot: InfoHotspot): void {
    this.paintCanvas(hotspot.label, hotspot.description);
    this.texture.upload();
    this.entity.enabled = true;
    this.visible = true;
    this.repositionInFrontOfCamera();
    if (this.hideTimer !== null) window.clearTimeout(this.hideTimer);
    this.hideTimer = window.setTimeout(() => this.hide(), AUTO_HIDE_MS);
  }

  hide(): void {
    this.entity.enabled = false;
    this.visible = false;
    if (this.hideTimer !== null) {
      window.clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
  }

  isVisible(): boolean {
    return this.visible;
  }

  destroy(): void {
    this.app.off('update', this.tick);
    this.hide();
    this.entity.destroy();
    this.texture.destroy();
    this.material.destroy();
  }

  // ─────────────────────────────────────────────────────────────────

  private readonly tick = (_dt: number): void => {
    if (!this.visible) return;
    // Re-billboard every frame so the card stays facing the camera as the
    // operator's head rotates. The plane's localEulerAngles -90° about X
    // makes it face +Z by default; lookAt aligns its -Z with the camera so
    // the front of the plane (where the canvas paints) is camera-facing.
    const camPos = this.camera.getPosition();
    this.entity.lookAt(camPos);
    // lookAt rotates so the entity's -Z points at camPos. The plane's face
    // is on +Y after the -90° X rotation, so we need an extra 90° around
    // local X to swing the face toward the camera. This is the standard
    // PlayCanvas plane-billboarding fix-up.
    this.entity.rotateLocal(90, 0, 0);
  };

  private repositionInFrontOfCamera(): void {
    const cam = this.camera;
    const camPos = cam.getPosition();
    const fwd = cam.forward;
    this.tmpVec3.set(
      camPos.x + fwd.x * CARD_DISTANCE_M,
      camPos.y + fwd.y * CARD_DISTANCE_M,
      camPos.z + fwd.z * CARD_DISTANCE_M,
    );
    this.entity.setPosition(this.tmpVec3);
  }

  private paintCanvas(title: string, body: string): void {
    const c = this.ctx;
    // Background — dark plate with subtle gradient + brand-cyan border.
    const grad = c.createLinearGradient(0, 0, 0, CANVAS_H);
    grad.addColorStop(0, '#0a1620');
    grad.addColorStop(1, '#020812');
    c.fillStyle = grad;
    c.fillRect(0, 0, CANVAS_W, CANVAS_H);

    // 4 px brand-cyan border so the card edges read clearly against the
    // panorama background.
    c.strokeStyle = '#4fc7ff';
    c.lineWidth = 4;
    c.strokeRect(2, 2, CANVAS_W - 4, CANVAS_H - 4);

    // Title.
    c.fillStyle = '#ffffff';
    c.font = 'bold 52px system-ui, -apple-system, "Segoe UI", sans-serif';
    c.textBaseline = 'top';
    wrapText(c, title, 48, 56, CANVAS_W - 96, 64);

    // Divider line.
    c.strokeStyle = 'rgba(255,255,255,0.16)';
    c.lineWidth = 2;
    c.beginPath();
    c.moveTo(48, 140);
    c.lineTo(CANVAS_W - 48, 140);
    c.stroke();

    // Body text — wraps to multiple lines.
    c.fillStyle = '#d8e0e8';
    c.font = '28px system-ui, -apple-system, "Segoe UI", sans-serif';
    wrapText(c, body, 48, 168, CANVAS_W - 96, 38);

    // Tiny footer hint so the operator knows it'll dismiss itself.
    c.fillStyle = 'rgba(255,255,255,0.42)';
    c.font = '18px system-ui, -apple-system, "Segoe UI", sans-serif';
    c.fillText(`Auto-dismiss em ${Math.round(AUTO_HIDE_MS / 1000)}s`, 48, CANVAS_H - 38);
  }
}

/** Minimal canvas text-wrap helper. Greedy word-wrap; truncates if it
 *  overflows the canvas height to keep the card readable. */
function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number,
): void {
  const words = text.split(/\s+/).filter(Boolean);
  let line = '';
  let cy = y;
  for (const word of words) {
    const test = line ? `${line} ${word}` : word;
    const w = ctx.measureText(test).width;
    if (w > maxWidth && line) {
      ctx.fillText(line, x, cy);
      line = word;
      cy += lineHeight;
      if (cy > CANVAS_H - lineHeight - 40) {
        ctx.fillText(`${line}…`, x, cy);
        return;
      }
    } else {
      line = test;
    }
  }
  if (line) ctx.fillText(line, x, cy);
}
